import { useState } from 'react'
import {
  resendEmailOtpService,
  verifyEmailOtpService,
} from '../../services/authService'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../ui/input-otp'

interface VerifyEmailModalProps {
  isOpen: boolean
  email: string
  onVerificationSuccess: () => void
  onClose: () => void
}

export default function VerifyEmailModal({
  isOpen,
  email,
  onVerificationSuccess,
  onClose,
}: VerifyEmailModalProps) {
  const [otp, setOtp] = useState('')
  const [isVerifying, setIsVerifying] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [error, setError] = useState('')
  const [resendMessage, setResendMessage] = useState('')

  if (!isOpen) return null

  const handleVerify = async () => {
    if (otp.length !== 4) {
      setError('Please enter the 4-digit verification code')
      return
    }

    setIsVerifying(true)
    setError('')

    try {
      const result = await verifyEmailOtpService(otp)

      if (result.error) {
        const errorData = result.error.response?.data
        const statusCode = result.error.response?.status
        console.log(
          'Email verification error data:',
          errorData,
          'Status:',
          statusCode,
        )

        // Handle 401 as success (email verified but not auto-logged in)
        if (statusCode === 401) {
          onVerificationSuccess()
          return
        }

        // Handle specific error cases
        let errorMessage = 'Invalid verification code. Please try again.'

        if (errorData?.errors?.[0]) {
          const firstError = errorData.errors[0]
          if (firstError.code === 'incorrect_code') {
            errorMessage =
              'The verification code you entered is incorrect. Please check your email and try again.'
          } else if (firstError.code === 'expired_code') {
            errorMessage =
              'This verification code has expired. Please request a new one.'
          } else {
            errorMessage = firstError.message || errorMessage
          }
        } else if (errorData?.detail) {
          errorMessage = errorData.detail
        } else if (errorData?.message) {
          errorMessage = errorData.message
        } else if (errorData?.non_field_errors?.[0]) {
          errorMessage = errorData.non_field_errors[0]
        }

        setError(errorMessage)
      } else {
        onVerificationSuccess()
      }
    } catch (err) {
      setError('Something went wrong. Please try again.')
    } finally {
      setIsVerifying(false)
    }
  }

  const handleResend = async () => {
    setIsResending(true)
    setError('')
    setResendMessage('')

    try {
      const result = await resendEmailOtpService(email)

      if (result.error) {
        const errorData = result.error.response?.data
        const errorMessage =
          errorData?.detail ||
          errorData?.message ||
          'Failed to resend verification code. Please try again.'
        setError(errorMessage)
      } else {
        setResendMessage('Verification code has been sent to your email')
        setOtp('') // Clear the current OTP input
      }
    } catch (err) {
      setError('Failed to resend verification code. Please try again.')
    } finally {
      setIsResending(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center pb-4">
          <CardTitle className="text-2xl font-bold text-center">
            Verify Your Email
          </CardTitle>
          <p className="text-gray-600 mt-2">
            We've sent a 4-digit verification code to
            <br />
            <span className="font-medium text-gray-900">{email}</span>
          </p>
          <p className="text-gray-600 mt-4 text-sm">
            In order for you to join and access the Ecosystem, you received a
            4-digit code in the email you registered with us. Please note the
            4-digit code will expire in 45 minutes.
          </p>
        </CardHeader>

        <CardContent className="space-y-6">
          <div className="flex flex-col items-center space-y-4">
            <div className="text-sm text-gray-600 text-center">
              Enter the verification code:
            </div>

            <InputOTP
              maxLength={4}
              value={otp}
              onChange={(value) => {
                setOtp(value)
                setError('') // Clear error when user starts typing
              }}
              className="justify-center"
            >
              <InputOTPGroup>
                <InputOTPSlot index={0} className="w-12 h-12 text-lg" />
                <InputOTPSlot index={1} className="w-12 h-12 text-lg" />
                <InputOTPSlot index={2} className="w-12 h-12 text-lg" />
                <InputOTPSlot index={3} className="w-12 h-12 text-lg" />
              </InputOTPGroup>
            </InputOTP>
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md border border-red-200 text-center">
              <i className="fa-solid fa-exclamation-triangle mr-2" />
              {error}
            </div>
          )}

          {resendMessage && (
            <div className="text-sm text-green-600 bg-green-50 p-3 rounded-md border border-green-200 text-center">
              <i className="fa-solid fa-check-circle mr-2" />
              {resendMessage}
            </div>
          )}

          <div className="space-y-3">
            <Button
              onClick={handleVerify}
              disabled={otp.length !== 4 || isVerifying}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              {isVerifying ? (
                <>
                  <i className="fa-duotone fa-solid fa-spinner-third fa-spin mr-2" />
                  Verifying...
                </>
              ) : (
                <>
                  <i className="fa-solid fa-envelope-check mr-2" />
                  Verify Email
                </>
              )}
            </Button>

            <div className="text-center">
              <p className="text-sm text-gray-600 mb-2">
                Didn't receive the code?
              </p>
              <button
                onClick={handleResend}
                disabled={isResending}
                className="text-sm text-blue-600 hover:underline disabled:text-gray-400"
              >
                {isResending ? (
                  <>
                    <i className="fa-duotone fa-solid fa-spinner-third fa-spin mr-1" />
                    Resending...
                  </>
                ) : (
                  'Resend verification code'
                )}
              </button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
